import { create } from 'zustand';
import type { Achievement, UserAchievement } from '@breath-lab/types';
import { getServices, type Unsubscribe } from '@breath-lab/services';

interface AchievementsState {
  achievements: Achievement[];
  userAchievements: UserAchievement[];
  isLoading: boolean;
  newlyUnlocked: UserAchievement[];

  // selectors
  getAchievementsForWorkout: (workoutId: string) => Achievement[];

  // actions
  load: (userId: string) => Promise<void>;
  startListening: (userId: string) => Promise<void>;
  stopListening: () => void;
  clearNewlyUnlocked: () => void;
}

// Stored outside Zustand state to avoid re-renders
let unsubscribe: Unsubscribe | null = null;

export const useAchievements = create<AchievementsState>((set, get) => ({
  achievements: [],
  userAchievements: [],
  isLoading: false,
  newlyUnlocked: [],

  getAchievementsForWorkout: (workoutId) => {
    const { achievements, userAchievements } = get();
    const matchingIds = new Set(
      userAchievements
        .filter((ua) => ua.awardedOn.includes(workoutId))
        .map((ua) => ua.id)
    );
    return achievements.filter((a) => matchingIds.has(a.id));
  },

  load: async (userId) => {
    if (unsubscribe) return; // already listening
    set({ isLoading: true });
    try {
      const achievements = await getServices().data.fetchAchievements();
      set({ achievements, isLoading: false });

      let isFirstSnapshot = true;
      unsubscribe = getServices().userData.listenUserAchievements(userId, (newList) => {
        if (isFirstSnapshot) {
          isFirstSnapshot = false;
          set({ userAchievements: newList });
          return;
        }
        const prev = get().userAchievements;
        const prevIds = new Set(prev.map((ua) => ua.id));
        const newlyUnlocked = newList.filter((ua) => !prevIds.has(ua.id));
        set((s) => ({
          userAchievements: newList,
          newlyUnlocked: [...s.newlyUnlocked, ...newlyUnlocked],
        }));
      });
    } catch {
      set({ isLoading: false });
    }
  },

  startListening: async (userId) => {
    return get().load(userId);
  },

  stopListening: () => {
    if (unsubscribe) {
      unsubscribe();
      unsubscribe = null;
    }
    set({ achievements: [], userAchievements: [], newlyUnlocked: [], isLoading: false });
  },

  clearNewlyUnlocked: () => set({ newlyUnlocked: [] }),
}));
